/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Injectable
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceHelper
} from '@insurance/helpers/insurance.helper';
import {
	IInsuranceEntityHierarchy
} from '@insurance/interfaces/insurance-entity-hierarchy';
import {
	IInsuranceKeyContacts
} from '@insurance/interfaces/insurance-key-contacts.interface';
import {
	IInsuranceServiceProvider
} from '@insurance/interfaces/insurance-service-provider.interface';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ApiHelper
} from '@shared/helpers/api.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IProductSetting
} from '@shared/interfaces/application-objects/product-setting.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ResolverService
} from '@shared/services/resolver.service';
import {
	DateTime
} from 'luxon';

/**
 * A class representing a common interface to gather fully populated
 * service provider information.
 *
 * @export
 * @class ServiceProviderService
 */
@Injectable()
export class ServiceProviderService
{
	/**
	 * Creates an instance of an ServiceProviderService.
	 *
	 * @param {InsuranceService} insuranceService
	 * The insurance service for this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service for this component.
	 * @param {Router} router
	 * The router service for this component.
	 * @param {ResolverService} resolverService
	 * The resolver service for this component.
	 * @param {ActivityService} activityService
	 * The activity service for this component.
	 * @memberof ServiceProviderService
	 */
	public constructor(
		public readonly insuranceService: InsuranceService,
		public readonly entityInstanceApiService: EntityInstanceApiService,
		public readonly router: Router,
		public readonly resolverService: ResolverService,
		public readonly activityService: ActivityService)
	{
	}

	/**
	 * Gets the service provider description matching the
	 * service provider identifier.
	 *
	 * @async
	 * @param {string} serviceProviderId
	 * The resource identifier of the service provider.
	 * @param {string} serviceProviderType
	 * The type of the service provider.
	 * @param {string} serviceProviderEffectiveDate
	 * The effective date of the service provider.
	 * @returns {Promise<string>}
	 * An awaitable description used to display a service provider.
	 * @memberof ServiceProviderService
	 */
	public async getServiceProviderDescription(
		serviceProviderId: string,
		serviceProviderType: string,
		serviceProviderEffectiveDate: string): Promise<string>
	{
		const serviceProviderInstance: IEntityInstance =
			await this.getServiceProviderInstance(
				<IInsuranceServiceProvider>
				{
					id: serviceProviderId,
					type: serviceProviderType
				});

		const serviceProviderDisplayName: string =
			this.getEntityDisplayName(
				serviceProviderInstance);

		if (AnyHelper.isNull(serviceProviderDisplayName))
		{
			return null;
		}

		return serviceProviderDisplayName
			+ ' - Effective: '
			+ StringHelper.format(
				serviceProviderEffectiveDate,
				AppConstants.formatTypes.shortDate);
	}

	/**
	 * Gets a detailed list of service providers living under
	 * a determined policy instance.
	 *
	 * @param {number} policyId
	 * The policy identifier.
	 *
	 * @async
	 * @returns {Promise<IInsuranceServiceProvider[]>}
	 * The detailed policy service providers, including the
	 * systematically created display name.
	 * @memberof ServiceProviderService
	 */
	public async getDetailedServiceProviders(
		policyId: number): Promise<IInsuranceServiceProvider[]>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups.policies;

		const policy: IEntityInstance =
			await this.entityInstanceApiService.get(policyId);

		const serviceProviders: IInsuranceServiceProvider[] =
			policy.data.serviceProviders;

		const detailedServiceProviders: IInsuranceServiceProvider[] = [];

		for (const serviceProvider of serviceProviders)
		{
			const serviceProviderInstance: IEntityInstance =
				await this.getServiceProviderInstance(
					serviceProvider);

			const serviceProviderDisplayName: string =
				this.getEntityDisplayName(serviceProviderInstance);

			detailedServiceProviders.push(
				{
					...serviceProvider,
					...{
						displayName:
							serviceProviderDisplayName,
						instanceId:
							serviceProviderInstance.id,
						entityType:
							serviceProviderInstance.entityType
					}
				});
		}

		return detailedServiceProviders;
	}

	/**
	 * Handles an activity to executes the ManageServiceProvider
	 * workflow action to add a new service provider to the policy.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {IInsuranceServiceProvider} serviceProvider
	 * The new service provider object.
	 * @memberof ServiceProviderService
	 */
	public async addServiceProvider(
		policyId: number,
		serviceProvider: IInsuranceServiceProvider): Promise<void>
	{
		const addServiceProviderPromise: Function =
			async() =>
			{
				const queryString: string =
					this.entityInstanceApiService.formUrlParam(
						AppConstants.empty,
						{
							serviceProviderAction:
								InsuranceConstants.serviceProviderActionNames
									.add,
							serviceProviderId: serviceProvider.id,
							serviceProviderType: serviceProvider.type,
							serviceProviderSubtype: serviceProvider.subtype,
							serviceProviderEffectiveDate:
								serviceProvider.effectiveDate
						});
				this.entityInstanceApiService.entityInstanceTypeGroup =
					InsuranceConstants.insuranceEntityTypeGroups.policies;

				await this.entityInstanceApiService.executeAction(
					policyId,
					'ManageServiceProvider',
					null,
					`${queryString}`);
			};

		await this.activityService.handleActivity(
			new Activity(
				addServiceProviderPromise(),
				'<strong>Adding Service Provider</strong>',
				'<strong>Added Service Provider</strong>',
				'Service Provider was successfully added.',
				'Service Provider was not added.'));
	}

	/**
	 * Given a policy id and/or set of policy service providers
	 * this method will get all the available instances split by
	 * type and subtype when applicable.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {string} type
	 * The service provider type.
	 * @param {string} subtype
	 * The service provider subtype.
	 * @param {string[]} parentAgencyResourceIds
	 * A list of resource ids representing the agencies as parents of
	 * the users that may be available to be added as a service provider.
	 * @param {string} apiFilter
	 * A custom api filter.
	 * @param {string} apiOrderBy
	 * A custom api order by.
	 * @param {boolean} excludeEffective
	 * whether to exclude the effective service providers from
	 * the results or not. Defaults to true.
	 * @returns {Promise<IEntityInstance[]>}
	 * An awaitable group of entity instances representing the
	 * service providers of type Agency and or Producer,
	 * available to add to the policy.
	 * @memberof ServiceProviderService
	 */
	public async getAvailableServiceProviderInstances(
		policyId: number,
		policyServiceProviders: IInsuranceServiceProvider[] = null,
		type: string = null,
		subtype: string = null,
		parentAgencyResourceIds: string[] = null,
		apiFilter: string = AppConstants.empty,
		excludeEffective: boolean = true): Promise<IEntityInstance[]>
	{
		const serviceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNullOrEmptyArray(policyServiceProviders)
				? await this.getDetailedServiceProviders(policyId)
				: policyServiceProviders;

		const agencyInstances: IEntityInstance[] =
			AnyHelper.isNull(type)
				|| type === InsuranceConstants.serviceProviderTypes.agency
				? await this.getAvailableAgencyServiceProviderInstances(
					policyId,
					subtype,
					serviceProviders,
					apiFilter,
					AppConstants.empty,
					excludeEffective)
				: [];

		const producerInstances: IEntityInstance[] =
			AnyHelper.isNull(type)
				|| type === InsuranceConstants.serviceProviderTypes.producer
				? await this.getAvailableProducerServiceProviderInstances(
					policyId,
					parentAgencyResourceIds,
					serviceProviders,
					apiFilter,
					AppConstants.empty,
					excludeEffective)
				: [];

		return [
			... agencyInstances,
			... producerInstances
		];
	}

	/**
	 * Gets the matching service providers by resource identifier.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string[]} resourceIdentifiers
	 * A list of resource identifiers deteremined to find
	 * matching service providers.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @returns {Promise<IInsuranceServiceProvider[]>}
	 * An awaitable set of insurance service providers
	 * matching the list of provided resource identifiers.
	 * @memberof ServiceProviderService
	 */
	public async getMatchingServiceProvidersByResourceId(
		policyId: number,
		resourceIdentifiers: string[],
		policyServiceProviders: IInsuranceServiceProvider[] = null):
			Promise<IInsuranceServiceProvider[]>
	{
		const serviceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNullOrEmptyArray(policyServiceProviders)
				? await this.getDetailedServiceProviders(policyId)
				: policyServiceProviders;

		return serviceProviders
			.filter(
				(serviceProvider: IInsuranceServiceProvider) =>
					resourceIdentifiers.some(
						(resourceId: string) =>
							serviceProvider.id === resourceId));
	}

	/**
	 * Gets the policy effective service providers.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string} type
	 * The service provider type. If null or not set it will get all
	 * effective items from the array no matter the type.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {boolean} includeFutureEffective
	 * Whether to include any future effective service provider or not.
	 * Defaults to false.
	 * @returns {Promise<IInsuranceServiceProvider>}
	 * The policy effective service providers.
	 * @memberof ServiceProviderService
	 */
	public async  getEffectiveServiceProviders(
		policyId: number,
		type: string = null,
		policyServiceProviders: IInsuranceServiceProvider[] = null,
		includeFutureEffective: boolean = false):
			Promise<IInsuranceServiceProvider[]>
	{
		const serviceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNullOrEmptyArray(policyServiceProviders)
				? await this.getDetailedServiceProviders(policyId)
				: policyServiceProviders;

		const agencyEffectiveServiceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNull(type)
				|| type
					=== InsuranceConstants.serviceProviderTypes.agency
				? await this.getAgencyEffectiveServiceProviders(
					policyId,
					serviceProviders)
				: [];

		const producerEffectiveServiceProvider: IInsuranceServiceProvider =
			AnyHelper.isNull(type)
				|| type
					=== InsuranceConstants.serviceProviderTypes.producer
				? await this.getEffectiveServiceProvider(
					policyId,
					InsuranceConstants.serviceProviderTypes.producer,
					null,
					serviceProviders)
				: null;

		const futureEffectiveServiceProviders: IInsuranceServiceProvider[] =
			includeFutureEffective
				? await this.getFutureEffectiveServiceProviders(
					policyId,
					type,
					null,
					serviceProviders)
				: [];

		return [
			... agencyEffectiveServiceProviders,
			... !AnyHelper.isNull(producerEffectiveServiceProvider)
				? [producerEffectiveServiceProvider]
				: [],
			... futureEffectiveServiceProviders
		];
	}

	/**
	 * Gets the effective service provider.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string} type
	 * The service provider type.
	 * @param {string} subtype
	 * The service provider subtype. If null or not set it will get all
	 * effective items from the array no matter the subtype.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 *
	 * @returns {IInsuranceServiceProvider}
	 * The effective service provider.
	 * @memberof ServiceProviderService
	 */
	public async getEffectiveServiceProvider(
		policyId: number,
		type: string,
		subtype: string = null,
		policyServiceProviders: IInsuranceServiceProvider[] = null):
			Promise<IInsuranceServiceProvider>
	{
		const serviceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNullOrEmptyArray(policyServiceProviders)
				? await this.getDetailedServiceProviders(policyId)
				: policyServiceProviders;

		const serviceProvidersByType: IInsuranceServiceProvider[] =
			this.getServiceProvidersByType(
				serviceProviders,
				type,
				subtype);

		const comparisonDate: DateTime =
			await this.getServiceProviderComparisonDate(policyId);

		const pastServiceProviders: IInsuranceServiceProvider[] =
			serviceProvidersByType.filter(
				(serviceProvider: IInsuranceServiceProvider) =>
					DateHelper.fromUtcIso(serviceProvider.effectiveDate)
						<= comparisonDate);

		const reduceToMostRecent: Function =
			(previous: IInsuranceServiceProvider,
				current: IInsuranceServiceProvider) =>
				+DateHelper.fromUtcIso(current.effectiveDate)
					> +DateHelper.fromUtcIso(previous.effectiveDate)
					? current
					: previous;

		return !AnyHelper.isNullOrEmptyArray(pastServiceProviders)
			? pastServiceProviders
				.reduce(
					(previous: IInsuranceServiceProvider,
						current: IInsuranceServiceProvider) =>
						reduceToMostRecent(previous, current))
			: null;
	}

	/**
	 * Gets the future effective service providers.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string} type
	 * The service provider type. If null or not set it will get all
	 * effective items from the array no matter the type.
	 * @param {string} subtype
	 * The service provider subtype. If null or not set it will get all
	 * effective items from the array no matter the subtype.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @returns {Promise<IInsuranceServiceProvider>}
	 * The policy future effective service providers.
	 * @memberof ServiceProviderService
	 */
	public async getFutureEffectiveServiceProviders(
		policyId: number,
		type: string = null,
		subtype: string = null,
		policyServiceProviders: IInsuranceServiceProvider[] = null):
			Promise<IInsuranceServiceProvider[]>
	{
		const serviceProviders: IInsuranceServiceProvider[] =
			AnyHelper.isNullOrEmptyArray(policyServiceProviders)
				? await this.getDetailedServiceProviders(policyId)
				: policyServiceProviders;

		const serviceProvidersByType: IInsuranceServiceProvider[] =
			this.getServiceProvidersByType(
				serviceProviders,
				type,
				subtype);
		const comparisonDate: DateTime =
			await this.getServiceProviderComparisonDate(policyId);

		return serviceProvidersByType.filter(
			(serviceProvider: IInsuranceServiceProvider) =>
				DateHelper.fromUtcIso(serviceProvider.effectiveDate)
				> comparisonDate);
	}

	/**
	 * Gets the entity instances
	 * for a list of service providers.
	 *
	 * @param {IInsuranceServiceProvider[]} serviceProviders
	 * The service provider object.
	 *
	 * @async
	 * @returns {Promise<IEntityInstance[]>}
	 * The entity instances matching the service providers.
	 * @memberof ServiceProviderService
	 */
	public async getServiceProvidersInstances(
		serviceProviders: IInsuranceServiceProvider[]):
			Promise<IEntityInstance[]>
	{
		const promiseArray: Promise<IEntityInstance>[] = [];
		for(const serviceProvider of serviceProviders)
		{
			promiseArray.push(
				this.getServiceProviderInstance(serviceProvider));
		}

		return Promise.all(promiseArray);
	}

	/**
	 * Gets the Service Provider Instance
	 * by querying the resource identifier and finding
	 * a single query result matching.
	 *
	 * @param {IInsuranceServiceProvider} serviceProvider
	 * The service provider object.
	 * @param {string} entityTypeGroup
	 * The entity type group. This is null by default.
	 * When null it will systematically set the right type group
	 * based on the service provider type.
	 *
	 * @async
	 * @returns {Promise<IEntityInstance>}
	 * The service provider entity instance.
	 * Will return null when no results were found.
	 * @memberof ServiceProviderService
	 */
	public async getServiceProviderInstance(
		serviceProvider: IInsuranceServiceProvider,
		entityTypeGroup: string = null): Promise<IEntityInstance>
	{
		const serviceProviderEntityTypeGroup: string =
			serviceProvider.type
				=== InsuranceConstants.serviceProviderTypes.agency
				? InsuranceConstants.insuranceEntityTypeGroups.agencies
				: InsuranceConstants.insuranceEntityTypeGroups.users;

		this.entityInstanceApiService.entityInstanceTypeGroup =
			AnyHelper.isNull(entityTypeGroup)
				? serviceProviderEntityTypeGroup
				: entityTypeGroup;

		return this.entityInstanceApiService
			.getSingleQueryResult(
				`${AppConstants.commonProperties.resourceIdentifier} `
					+ `${AppConstants.filterQueryOperators.equal} `
					+ `'${serviceProvider.id}'`,
				AppConstants.empty,
				true);
	}

	/**
	 * Gets the entity display name.
	 *
	 * @async
	 * @param {IEntityInstance} entityInstance
	 * The entity instance.
	 * @returns {string}
	 * The display name.
	 * @memberof ServiceProviderService
	 */
	public getEntityDisplayName(
		entityInstance: IEntityInstance): string
	{
		if (AnyHelper.isNull(entityInstance?.data))
		{
			return null;
		}

		return InsuranceHelper
			.getEntityDisplayName(entityInstance);
	}

	/**
	 * Gets whether a product setting is active or service existing by product.
	 *
	 * @async
	 * @param {IProductSetting[]} productSetting
	 * The product settings.
	 * @param {IEntityInstance} product
	 * The product instance.
	 * @memberof ServiceProviderService
	 */
	public isActiveProductSettingByProduct(
		productSetting: IProductSetting,
		product: IEntityInstance): boolean
	{
		return productSetting.name === product.data.name
			&& (productSetting.authority
				=== InsuranceConstants.productSettingAuthorities.active
				|| productSetting.authority
					=== InsuranceConstants.productSettingAuthorities
						.serviceExisting);
	}

	/**
	 * Given a policy id, service provider type, and a service
	 * provider subtype this will load and return the active(effective)
	 * associated entity instance matching the provider type and id.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy data holding service providers.
	 * @param {string} serviceProviderType
	 * The policyTermTransaction data holding transaction type.
	 * @param {string} serviceProviderSubtype
	 * The service provider type to load an entity instance for.
	 * @returns {Promise<IEntityInstance>}
	 * An awaitable entity instance that represents the currently active
	 * service provider.
	 * @memberof ServiceProviderService
	 */
	public async getActiveServiceProviderInstance(
		policyId: number,
		serviceProviderType: string,
		serviceProviderSubtype: string = null): Promise<IEntityInstance>
	{
		const subtype: string =
			AnyHelper.isNullOrEmpty(serviceProviderSubtype)
				&& serviceProviderType ===
					InsuranceConstants.serviceProviderTypes.agency
				? InsuranceConstants.serviceProviderSubtypes.retail
				: null;

		const effectiveServiceProvider =
			await this.getEffectiveServiceProvider(
				policyId,
				serviceProviderType,
				subtype);

		return this.getServiceProviderInstance(
			effectiveServiceProvider);
	}

	/**
	 * Gets key contacts data from a transaction, term, or policy instance.
	 *
	 * @async
	 * @param {IEntityInstance} entity
	 * The entity to get key contacts for.
	 * @returns {Promise<IInsuranceKeyContacts>}
	 * An awaitable key contacts object holding product, agency, and
	 * primary insured information.
	 * @memberof ServiceProviderService
	 */
	public async getKeyContacts(
		entity: IEntityInstance): Promise<IInsuranceKeyContacts>
	{
		const insuranceEntityHierarchy: IInsuranceEntityHierarchy =
			await this.insuranceService
				.getInsuranceEntityHierarchy(
					entity.id,
					entity.entityType);

		const agencyEffectiveServiceProviders: IInsuranceServiceProvider[] =
			await this.getEffectiveServiceProviders(
				insuranceEntityHierarchy.policy.id,
				InsuranceConstants.serviceProviderTypes.agency);

		const effectiveAgencyInstances: IEntityInstance[] =
			await this.getServiceProvidersInstances(
				agencyEffectiveServiceProviders);

		const insuranceKeyContacts: any =
			{
				agencies: [...effectiveAgencyInstances],
				producer:
					await this.getActiveServiceProviderInstance(
						insuranceEntityHierarchy.policy.id,
						InsuranceConstants.serviceProviderTypes.producer),
				primaryInsured:
					insuranceEntityHierarchy
						.policyTermTransaction.data.interests[0]
			};

		return insuranceKeyContacts;
	}

	/**
	 * Gets the agency effective service providers.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {IInsuranceServiceProvider[]} serviceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @returns {Promise<IInsuranceServiceProvider>}
	 * The effective service providers split by subtype.
	 * Applicable to Agency.
	 * type only
	 * @memberof ServiceProviderService
	 */
	private async getAgencyEffectiveServiceProviders(
		policyId: number,
		serviceProviders: IInsuranceServiceProvider[]):
			Promise<IInsuranceServiceProvider[]>
	{
		const effectiveServiceProviders: IInsuranceServiceProvider[] = [];
		for(const subtype
			of Object.keys(InsuranceConstants.serviceProviderSubtypes))
		{
			const effectiveServiceProviderBySubType =
				await this.getEffectiveServiceProvider(
					policyId,
					InsuranceConstants.serviceProviderTypes.agency,
					InsuranceConstants.serviceProviderSubtypes[subtype],
					serviceProviders);

			if (!AnyHelper.isNull(effectiveServiceProviderBySubType))
			{
				effectiveServiceProviders.push(
					effectiveServiceProviderBySubType);
			}
		}

		return effectiveServiceProviders;
	}

	/**
	 * Gets the exclude service providers filter.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string} type
	 * The service provider type.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @returns {Promise<string>}
	 * The service providers query that will filter from an api call
	 * those ids that need to be excluded as are already effective.
	 * @memberof ServiceProviderService
	 */
	private async getExcludeEffectiveProvidersFilter(
		policyId: number,
		type: string,
		policyServiceProviders: IInsuranceServiceProvider[]): Promise<string>
	{
		const effectiveServiceProviders: IInsuranceServiceProvider[] =
			await this.getEffectiveServiceProviders(
				policyId,
				type,
				policyServiceProviders);

		let excludeEffectiveFilter: string = AppConstants.empty;
		effectiveServiceProviders.forEach(
			(effectiveProvider: any) =>
			{
				const serviceProviderInstanceId: number =
					effectiveProvider.instanceId;
				const commonFilterPart: string =
					`${AppConstants.commonProperties.id} `
							+ `${AppConstants.filterQueryOperators.notEqual} `
							+ `${serviceProviderInstanceId}`;
				excludeEffectiveFilter =
					AnyHelper.isNullOrEmpty(excludeEffectiveFilter)
						? commonFilterPart
						: `${excludeEffectiveFilter} `
							+ `${AppConstants.filterQueryOperators.and} `
							+ commonFilterPart;
			});

		return excludeEffectiveFilter;
	}

	/**
	 * Gets the service providers by type.
	 *
	 * @async
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {string} type
	 * The service provider type. If null or not set it will get all
	 * effective items from the array no matter the type.
	 * @param {string} subtype
	 * The service provider subtype. If null or not set it will get all
	 * effective items from the array no matter the subtype.
	 *
	 * @returns {IInsuranceServiceProvider}
	 * A set of insurance service providers split by type.
	 * @memberof ServiceProviderService
	 */
	private getServiceProvidersByType(
		policyServiceProviders: IInsuranceServiceProvider[],
		type: string,
		subtype: string): IInsuranceServiceProvider[]
	{
		return policyServiceProviders
			.filter((serviceProvider: IInsuranceServiceProvider) =>
				(AnyHelper.isNull(type)
					|| serviceProvider.type === type)
					&& (AnyHelper.isNull(subtype)
						|| serviceProvider.subtype === subtype));
	}

	/**
	 * Gets the matching service providers by resource identifier.
	 *
	 * @async
	 * @param {IInsuranceServiceProvider[]} agencyServiceProviders
	 * The policy agency service providers used to get the user children from.
	 * @param {number} policyId
	 * The policy id.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {string} apiFilter
	 * A custom api filter.
	 * @param {string} apiOrderBy
	 * A custom api order by.
	 * @param {boolean} excludeEffective
	 * whether to exclude the effective service providers from
	 * the results or not. Defaults to true.
	 * @returns {Promise<IEntityInstance[]>}
	 * An awaitable set of producer(user) entity instances that are children
	 * of a set of agencies.
	 * @memberof ServiceProviderService
	 */
	private async getProducersInstancesAsAgencyChildren(
		agencyServiceProviders: IInsuranceServiceProvider[],
		policyId: number,
		policyServiceProviders: IInsuranceServiceProvider[],
		apiFilter: string,
		apiOrderBy: string,
		excludeEffective: boolean): Promise<IEntityInstance[]>
	{
		const parentIds: number[] =
			[
				...new Set(agencyServiceProviders.map(
					(serviceProvider: any) =>
						serviceProvider.instanceId))
			];

		let filterQuery: string =
			!AnyHelper.isNullOrEmptyArray(parentIds)
				? `Parents.Any(ParentInstanceId in (${parentIds})) `
				: AppConstants.empty;

		filterQuery =
			!AnyHelper.isNullOrEmpty(apiFilter)
				? filterQuery
					+ ` AND ${apiFilter}`
				: filterQuery;

		if(excludeEffective)
		{
			const excludeEffectiveFilter: string =
				await this.getExcludeEffectiveProvidersFilter(
					policyId,
					InsuranceConstants.serviceProviderTypes.producer,
					policyServiceProviders);

			filterQuery =
				!AnyHelper.isNullOrEmpty(excludeEffectiveFilter)
					?	excludeEffectiveFilter
						+ ` AND ${filterQuery}`
					: filterQuery;
		}

		return ApiHelper.getFullDataSet(
			this.entityInstanceApiService,
			filterQuery,
			apiOrderBy,
			InsuranceConstants.insuranceEntityTypeGroups.users,
			AppConstants.dataLimits.small);
	}

	/**
	 * Gets the agency instances available to be added
	 * as a service provider to the policy
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string} subtype
	 * The service provider subtype.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {string} subtype
	 * The service provider subtype.
	 * @param {string} apiFilter
	 * A custom api filter.
	 * @param {string} apiOrderBy
	 * A custom api order by.
	 * @param {boolean} excludeEffective
	 * whether to exclude the effective service providers from
	 * the results or not. Defaults to true.
	 * @returns {Promise<IEntityInstance[]>}
	 * An awaitable group of entity instances representing the
	 * service providers of type Agency, available to add to the policy.
	 * @memberof ServiceProviderService
	 */
	private async getAvailableAgencyServiceProviderInstances(
		policyId: number,
		subtype: string,
		policyServiceProviders: IInsuranceServiceProvider[],
		apiFilter: string,
		apiOrderBy: string,
		excludeEffective: boolean): Promise<IEntityInstance[]>
	{
		const transaction: IEntityInstance =
			await this.insuranceService
				.getLatestPolicyTermTransactionByPolicy(policyId);
		const product: IEntityInstance =
			await this.insuranceService
				.getProductByTransaction(transaction);

		let filterQuery: string = 'status eq \'Active\' ';

		filterQuery =
			!AnyHelper.isNull(subtype)
				? filterQuery
					+ `AND type eq '${subtype}' `
				: filterQuery;

		filterQuery =
			!AnyHelper.isNullOrEmpty(apiFilter)
				? filterQuery
					+ ` AND ${apiFilter}`
				: filterQuery;

		if(excludeEffective)
		{
			const excludeEffectiveFilter: string =
				await this.getExcludeEffectiveProvidersFilter(
					policyId,
					InsuranceConstants.serviceProviderTypes.agency,
					policyServiceProviders);

			filterQuery =
				!AnyHelper.isNullOrEmpty(excludeEffectiveFilter)
					?	excludeEffectiveFilter
						+ ` AND ${filterQuery}`
					: filterQuery;
		}

		const agencies: IEntityInstance[] =
			await ApiHelper.getFullDataSet(
				this.entityInstanceApiService,
				filterQuery,
				apiOrderBy,
				InsuranceConstants.insuranceEntityTypeGroups.agencies);

		return agencies.filter(
			(agency: IEntityInstance) =>
				agency.data.productSettings.some(
					(productSetting: IProductSetting) =>
						this.isActiveProductSettingByProduct(
							productSetting,
							product)));
	}

	/**
	 * Gets the producer(user) instances available to be added
	 * as a service provider to the policy.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 * @param {string[]} parentAgencyResourceIds
	 * A list of resource ids representing the agencies as parents of
	 * the users that may be available to be added as a service provider.
	 * @param {IInsuranceServiceProvider[]} policyServiceProviders
	 * The set of policy service providers. If null or not provided then
	 * it will go and get the data from the policy id.
	 * @param {string} apiFilter
	 * A custom api filter.
	 * @param {string} apiOrderBy
	 * A custom api order by.
	 * @param {boolean} excludeEffective
	 * whether to exclude the effective service providers from
	 * the results or not. Defaults to true.
	 * @returns {Promise<IEntityInstance[]>}
	 * An awaitable group of entity instances representing the
	 * service providers of type Producer(user), available to add to the policy.
	 * @memberof ServiceProviderService
	 */
	private async getAvailableProducerServiceProviderInstances(
		policyId: number,
		parentAgencyResourceIds: string[],
		policyServiceProviders: IInsuranceServiceProvider[],
		apiFilter: string,
		apiOrderBy: string,
		excludeEffective: boolean): Promise<IEntityInstance[]>
	{
		const parentAgencyServiceProviders: IInsuranceServiceProvider[] =
			!AnyHelper.isNullOrEmptyArray(parentAgencyResourceIds)
				? await this.getMatchingServiceProvidersByResourceId(
					policyId,
					parentAgencyResourceIds,
					policyServiceProviders)
				: await this.getEffectiveServiceProviders(
					policyId,
					InsuranceConstants.serviceProviderTypes.agency,
					policyServiceProviders);

		return this.getProducersInstancesAsAgencyChildren(
			parentAgencyServiceProviders,
			policyId,
			policyServiceProviders,
			apiFilter,
			apiOrderBy,
			excludeEffective);
	}

	/**
	 * Gets service provider comparison date used to get either
	 * the effective or the future effective service providers.
	 *
	 * @async
	 * @param {number} policyId
	 * The policy id.
	 *
	 * @returns {Promise<DateTime>}
	 * The comparisson date used for service provider reference.
	 * @memberof ServiceProviderService
	 */
	private async getServiceProviderComparisonDate(
		policyId: number): Promise<DateTime>
	{
		const policyTerm: IEntityInstance =
			await this.insuranceService
				.getLatestPolicyTermByPolicyId(policyId);
		const termEffectiveDate: DateTime =
			DateHelper
				.fromUtcIso(policyTerm.data.effectiveDate);
		const current: DateTime =
			DateHelper
				.getSystemDateTime();

		return current < termEffectiveDate
			? termEffectiveDate
			: current;
	}
}